import { useState, useEffect, useCallback, useRef } from "react";
import { X, Search, Users, MessageCircle, Check, Loader2 } from "lucide-react";
import {
  searchTeamUsers,
  getOrCreateDM,
  createGroup,
} from "../../../../api/teamChat";

/**
 * NewConversationModal — Unified search for creating DMs and Groups.
 *
 * Visual language matches the rest of team chat:
 *   - rounded-2xl floating card (same as TeamChatHeader / ChatEntry)
 *   - design tokens (surface / mainText / mutedText / velBlue / etc)
 *   - rounded-full pills, rounded-xl inputs, soft hovers
 *
 * Select 1 person → "Message [Name]" button (creates DM)
 * Select 2+ people → group name input + "Create Group" button
 *
 * Props:
 *   onClose: () => void
 *   onConversationCreated: (conversation, type) => void
 */
const NewConversationModal = ({ onClose, onConversationCreated }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);

  const isGroup = selectedUsers.length >= 2;

  // Focus search on mount
  useEffect(() => {
    setTimeout(() => searchInputRef.current?.focus(), 100);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchTeamUsers(query.trim());
        setResults(res?.data?.users || []);
      } catch (e) {
        console.error("Search failed:", e);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const toggleUser = useCallback((user) => {
    setSelectedUsers((prev) => {
      const exists = prev.find((u) => u.uuid === user.uuid);
      if (exists) return prev.filter((u) => u.uuid !== user.uuid);
      return [...prev, user];
    });
    setError("");
  }, []);

  const isSelected = useCallback(
    (uuid) => selectedUsers.some((u) => u.uuid === uuid),
    [selectedUsers],
  );

  const handleCreate = useCallback(async () => {
    if (selectedUsers.length === 0) return;

    setCreating(true);
    setError("");

    try {
      if (selectedUsers.length === 1) {
        const res = await getOrCreateDM(selectedUsers[0].uuid);
        const conv = res?.data?.conversation;
        if (conv) {
          onConversationCreated?.(conv, "dm");
          onClose();
        } else {
          setError("Failed to create conversation");
        }
      } else {
        const name =
          groupName.trim() ||
          selectedUsers.map((u) => u.display_name || u.first_name).join(", ");
        const memberUuids = selectedUsers.map((u) => u.uuid);
        const res = await createGroup(name, memberUuids);
        const conv = res?.data?.conversation;
        if (conv) {
          onConversationCreated?.(conv, "group");
          onClose();
        } else {
          setError("Failed to create group");
        }
      }
    } catch (e) {
      console.error("Failed to create conversation:", e);
      setError(e?.response?.data?.error || "Something went wrong");
    } finally {
      setCreating(false);
    }
  }, [selectedUsers, groupName, onClose, onConversationCreated]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className={[
        "w-[420px] max-h-[520px] flex flex-col font-poppins",
        "rounded-2xl bg-surface/90 backdrop-blur-md",
        "border border-surfaceBorder",
        "shadow-[0_4px_24px_rgba(0,0,0,0.08)]",
        "dark:shadow-[0_4px_24px_rgba(0,0,0,0.45)]",
        "overflow-hidden",
      ].join(" ")}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surfaceBorder shrink-0">
        <div className="flex items-center gap-x-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-velBlue/15 shrink-0">
            <MessageCircle
              className="h-3.5 w-3.5 text-velBlue"
              strokeWidth={2}
            />
          </div>
          <h3 className="text-[13px] font-normal tracking-wide text-mainText">
            New Conversation
          </h3>
        </div>
        <button
          onClick={onClose}
          className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-mainText/[0.06] transition text-mutedText hover:text-mainText cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>

      {/* Selected users — pill chips */}
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 pt-3">
          {selectedUsers.map((u) => (
            <span
              key={u.uuid}
              className="inline-flex items-center gap-x-1 pl-2 pr-1 py-0.5 rounded-full bg-velBlue/10 border border-velBlue/30 text-velBlue text-[11px] font-normal"
            >
              {u.display_name || u.first_name}
              <button
                onClick={() => toggleUser(u)}
                className="h-4 w-4 inline-flex items-center justify-center rounded-full hover:bg-velBlue/20 transition cursor-pointer"
                aria-label="Remove"
              >
                <X className="h-2.5 w-2.5" strokeWidth={2.5} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Group name input — only when 2+ selected */}
      {isGroup && (
        <div className="px-4 pt-2.5">
          <input
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Group name (optional)"
            className="w-full px-3 py-1.5 rounded-lg border border-inputBorder bg-inputBg/40 text-[12px] font-normal text-mainText placeholder:text-mutedText/70 focus:outline-none focus:ring-2 focus:ring-inputFocus/40 focus:border-inputFocus/60 transition"
          />
        </div>
      )}

      {/* Search input */}
      <div className="px-4 pt-3 pb-2">
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-mutedText/70"
            strokeWidth={2}
          />
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search people..."
            className="w-full pl-9 pr-9 py-2 rounded-lg border border-inputBorder bg-inputBg/40 text-[13px] font-normal text-mainText placeholder:text-mutedText/70 focus:outline-none focus:ring-2 focus:ring-inputFocus/40 focus:border-inputFocus/60 transition"
          />
          {searching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-mutedText/70 animate-spin" />
          )}
        </div>
      </div>

      {/* Results list */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-2 pb-2 min-h-0">
        {results.length === 0 && query.trim() && !searching && (
          <p className="px-2 py-6 text-center text-[12px] font-light text-mutedText/70">
            No users found
          </p>
        )}
        {results.length === 0 && !query.trim() && (
          <p className="px-2 py-6 text-center text-[12px] font-light text-mutedText/70">
            Search for people to message
          </p>
        )}
        {results.map((user) => {
          const displayName = user.display_name || user.first_name || "Unknown";
          const initial = (displayName[0] || "?").toUpperCase();
          const selected = isSelected(user.uuid);

          return (
            <button
              key={user.uuid}
              onClick={() => toggleUser(user)}
              disabled={creating}
              className={[
                "w-full flex items-center gap-x-3 px-3 py-2 rounded-lg text-left transition-colors cursor-pointer",
                selected ? "bg-velBlue/10" : "hover:bg-mainText/[0.04]",
                creating ? "opacity-50 cursor-not-allowed" : "",
              ].join(" ")}
            >
              <span
                className={[
                  "w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-normal shrink-0 transition-colors",
                  selected
                    ? "bg-velBlue/20 text-velBlue"
                    : "bg-mutedText/20 text-mutedText",
                ].join(" ")}
              >
                {initial}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-normal text-mainText truncate">
                  {displayName}
                </p>
                {user.email && (
                  <p className="text-[11px] font-light text-mutedText/70 truncate">
                    {user.email}
                  </p>
                )}
              </div>
              <span
                className={[
                  "w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors",
                  selected ? "bg-velBlue border-velBlue" : "border-inputBorder",
                ].join(" ")}
              >
                {selected && (
                  <Check className="h-3 w-3 text-white" strokeWidth={3} />
                )}
              </span>
            </button>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 py-2 shrink-0">
          <p className="text-[11px] font-normal text-logoutColor">{error}</p>
        </div>
      )}

      {/* Action button — adapts to selection count */}
      {selectedUsers.length > 0 && (
        <div className="px-4 py-3 border-t border-surfaceBorder shrink-0">
          <button
            onClick={handleCreate}
            disabled={creating}
            className="w-full flex items-center justify-center gap-x-2 py-2 rounded-full bg-velBlue hover:bg-velBlue/90 text-white text-[13px] font-normal tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
          >
            {creating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isGroup ? (
              <Users className="h-4 w-4" strokeWidth={2} />
            ) : (
              <MessageCircle className="h-4 w-4" strokeWidth={2} />
            )}
            {isGroup
              ? `Create Group (${selectedUsers.length} members)`
              : `Message ${selectedUsers[0].display_name || selectedUsers[0].first_name}`}
          </button>
        </div>
      )}
    </div>
  );
};

export default NewConversationModal;
